import React, { useState } from 'react';
import CreateAccountPrompt from '../components/CreateAccountPrompt';
import axios from 'axios';
import { getTurnstileToken } from '../lib/turnstile';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight, ArrowLeft, Check, Sparkles, Target, TrendingUp, Compass, Users as UsersIcon, Trophy, AlertCircle, Video, CalendarPlus, Mail } from 'lucide-react';
import { markSessionBooked } from '../lib/leadMagnetSuppression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The free Jumpstart maps to the DCA brand's free consulting service (shares the same 6 intake questions).
const REQUEST_BRAND_SLUG = 'dca';
const DEFAULT_FREE_SERVICE_ID = 'svc_d9ae6d16f4';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BUSINESS_Q = [
  {
    key: 'short_term_vision',
    icon: TrendingUp,
    label: 'What are your short-term goals for the next 30, 60, or 90 days?',
    placeholder: 'e.g. Launch my LLC, hit $5k MRR, get my first 10 paying clients...',
    minLen: 10,
  },
  {
    key: 'long_term_vision',
    icon: Compass,
    label: 'What is your long-term vision for the next 1-2 years?',
    placeholder: 'Where do you see your business and life in 1-2 years?',
    minLen: 10,
  },
  {
    key: 'biggest_struggle',
    icon: AlertCircle,
    label: 'What is the biggest struggle holding you back right now?',
    placeholder: 'Launching, finding customers, pricing, marketing, systems, mindset...',
    minLen: 10,
  },
  {
    key: 'current_stage',
    icon: Target,
    label: 'Which stage of business are you currently in?',
    options: [
      'Just an idea',
      'Validating my idea',
      'Pre-launch / building credibility',
      'Launched, finding first customers',
      'Running, ready to scale',
      'Stuck / pivoting',
    ],
  },
  {
    key: 'target_customer',
    icon: UsersIcon,
    label: 'Who is your ideal customer or client?',
    placeholder: 'Describe the people you want to serve and the problem you solve for them.',
    minLen: 10,
  },
  {
    key: 'success_definition',
    icon: Trophy,
    label: 'What would make this strategy session a 10/10 success for you?',
    placeholder: 'A clear plan, a key decision, a specific roadmap, accountability...',
    minLen: 10,
  },
];

const HEALTH_Q = [
  { key: 'short_term_vision', icon: TrendingUp, label: 'What health goals do you want to reach in the next 30, 60, or 90 days?', placeholder: 'e.g. Lose 15 lbs, walk daily, drink more water, sleep 7 hours a night...', minLen: 10 },
  { key: 'long_term_vision', icon: Compass, label: 'What does your healthiest self look like 1-2 years from now?', placeholder: 'How do you want to feel, move and live?', minLen: 10 },
  { key: 'biggest_struggle', icon: AlertCircle, label: 'What is the biggest health struggle holding you back right now?', placeholder: 'Consistency, cravings, time, energy, motivation, injuries...', minLen: 10 },
  { key: 'current_stage', icon: Target, label: 'Where are you in your health journey right now?', options: ['Just getting started', 'Restarting after a break', 'Somewhat consistent, need structure', 'Consistent, ready for the next level', 'Working around a health condition', 'Stuck or plateaued'], other: true },
  { key: 'target_customer', icon: UsersIcon, label: 'What does your current routine look like (eating, movement, sleep, stress)?', placeholder: 'Describe a typical day for you.', minLen: 10 },
  { key: 'success_definition', icon: Trophy, label: 'What would make this session a 10/10 success for you?', placeholder: 'A simple plan, accountability, a clear first step...', minLen: 10 },
];

const LIFE_Q = [
  { key: 'short_term_vision', icon: TrendingUp, label: 'What would you like to change or improve in your life in the next 30, 60, or 90 days?', placeholder: 'e.g. Build a morning routine, improve a relationship, find more balance...', minLen: 10 },
  { key: 'long_term_vision', icon: Compass, label: 'What is your vision for your life 1-2 years from now?', placeholder: 'Who do you want to become and how do you want your life to look?', minLen: 10 },
  { key: 'biggest_struggle', icon: AlertCircle, label: 'What is the biggest life struggle holding you back right now?', placeholder: 'Mindset, time, relationships, purpose, faith, finances, overwhelm...', minLen: 10 },
  { key: 'current_stage', icon: Target, label: 'Which area of your life needs the most attention right now?', options: ['Purpose and direction', 'Mindset and confidence', 'Relationships and family', 'Faith and spiritual growth', 'Time and priorities', 'Personal finances'], other: true },
  { key: 'target_customer', icon: UsersIcon, label: 'What support system do you have in your life right now?', placeholder: 'Family, friends, mentors, community, or none yet...', minLen: 10 },
  { key: 'success_definition', icon: Trophy, label: 'What would make this session a 10/10 success for you?', placeholder: 'Clarity, a decision, a plan, accountability...', minLen: 10 },
];

const VARIANTS = {
  business: { name: 'Business Jumpstart Session', sessionType: 'strategy_session', kind: null, lower: 'business jumpstart', confirm: 'Confirm My Strategy Session', questions: BUSINESS_Q },
  nonprofit: { name: 'Seven Stage Nonprofit Jumpstart', sessionType: 'nonprofit_jumpstart', kind: null, lower: 'nonprofit jumpstart', confirm: 'Confirm My Nonprofit Jumpstart', questions: BUSINESS_Q },
  health: { name: 'Health Jumpstart Session', sessionType: 'health_jumpstart', kind: 'Health', lower: 'health jumpstart', confirm: 'Confirm My Health Jumpstart', questions: HEALTH_Q, color: '#4ADE80', topic: 'your health goals' },
  life: { name: 'Life Jumpstart Session', sessionType: 'life_jumpstart', kind: 'Life', lower: 'life jumpstart', confirm: 'Confirm My Life Jumpstart', questions: LIFE_Q, color: '#60A5FA', topic: 'your life goals' },
};

const OTHER = 'Other: ';

// Choice buttons + optional "Other" with a free-text box.
function OptionPicker({ q, value, onChange, testPrefix }) {
  const isOther = (value || '').startsWith(OTHER);
  const btn = (on) => `text-left text-sm py-2 px-3 rounded-lg border transition-all ${on ? 'bg-[#EFF6FF] border-[#1D4ED8] text-[#1D4ED8] font-semibold' : 'bg-white border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8]'}`;
  return (
    <div className="ml-9">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {q.options.map((opt) => (
          <button key={opt} type="button" onClick={() => onChange(opt)} className={btn(value === opt)}
            data-testid={`${testPrefix}-${q.key}-opt-${opt.replace(/\s+/g, '-').toLowerCase()}`}>{opt}</button>
        ))}
        {q.other && <button type="button" onClick={() => !isOther && onChange(OTHER)} className={btn(isOther)} data-testid={`${testPrefix}-${q.key}-opt-other`}>Other</button>}
      </div>
      {q.other && isOther && (
        <Input autoFocus className="mt-2" placeholder="Please type your answer" value={value.slice(OTHER.length)}
          onChange={(e) => onChange(OTHER + e.target.value)} data-testid={`${testPrefix}-${q.key}-other-input`} />
      )}
    </div>
  );
}

const initialIntake = BUSINESS_Q.reduce((acc, q) => ({ ...acc, [q.key]: '' }), {});

export default function ScheduleSessionPage({ embed = false, onStepChange, variant = 'business' }) {
  const V = VARIANTS[variant] || VARIANTS.business;
  const intakeQuestions = V.questions;
  const brandName = V.name;
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', website: '' });
  const [intake, setIntake] = useState(initialIntake);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [reqDate, setReqDate] = useState('');
  const [reqTime, setReqTime] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [freeServiceId, setFreeServiceId] = useState(DEFAULT_FREE_SERVICE_ID);

  React.useEffect(() => {
    // Best-effort: resolve the DCA free service id so out-of-hours requests map correctly.
    axios.get(`${API_URL}/api/booking-brands/${REQUEST_BRAND_SLUG}`)
      .then((r) => {
        const free = (r.data.services || []).find((s) => Number(s.price) === 0) || (r.data.services || [])[0];
        if (free?.service_id) setFreeServiceId(free.service_id);
      })
      .catch(() => {});
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  React.useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth, year, month };
  };

  const handleDateClick = async (day) => {
    const { year, month } = getDaysInMonth(calendarMonth);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API_URL}/api/booking-slots/${dateStr}`);
      setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const intakeValid = () => {
    return intakeQuestions.every((q) => {
      const raw = intake[q.key] || '';
      const v = raw.trim();
      if (q.options) return raw.startsWith(OTHER) ? raw.slice(OTHER.length).trim().length >= 2 : v.length > 0;
      return v.length >= (q.minLen || 5);
    });
  };

  const tomorrowStr = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const submitTimeRequest = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!reqDate || !reqTime) { toast.error('Please choose your preferred date and time'); return; }
    if (!intakeValid()) { toast.error('Please answer all 6 questions'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/brand-time-request`, {
        brand_slug: REQUEST_BRAND_SLUG,
        service_id: freeServiceId,
        preferred_date: reqDate,
        preferred_time: reqTime,
        name: form.name, email: form.email, phone: form.phone,
        intake_responses: intake, notes: form.notes,
        session_kind: V.kind,
      });
      setRequestSent(true);
      setStep('request');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!intakeValid()) {
      toast.error('Please answer all 6 intake questions before confirming');
      return;
    }
    setSubmitting(true);
    try {
      const turnstile_token = await getTurnstileToken('booking');
      const res = await axios.post(`${API_URL}/api/book-slot`, {
        date: selectedDate,
        time: selectedSlot,
        ...form,
        turnstile_token,
        session_type: V.sessionType,
        intake,
      });
      setConfirmed({
        date: selectedDate,
        time: selectedSlot,
        booking_id: res.data.booking_id,
        zoom_join_url: res.data.zoom_join_url,
        zoom_password: res.data.zoom_password,
      });
      setStep(5);
      toast.success('Session confirmed! Check your email for details.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };

  const { firstDay, daysInMonth, year, month } = getDaysInMonth(calendarMonth);

  const stepLabels = [
    { n: 1, l: 'Date' },
    { n: 2, l: 'Time' },
    { n: 3, l: 'Details' },
    { n: 4, l: 'Intake' },
  ];

  return (
    <ConditionalLayout embed={embed} variant={variant}>
      <div className={embed ? '' : 'bg-[#FAFAFA] min-h-screen'} data-testid="schedule-session-page">
        {!embed && (
          <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-14 sm:py-20">
            {/* Subtle pattern + glow */}
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
              backgroundSize: '32px 32px'
            }}></div>
            <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-[#1D4ED8] opacity-30 blur-3xl"></div>
            <div className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>

            <div className="container-custom relative z-10 text-center max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F97316]/20 text-[#FB923C] text-xs sm:text-sm font-bold rounded-full mb-4 border border-[#F97316]/30 backdrop-blur-sm" data-testid="schedule-badge">
                <Sparkles size={14} /> 100% FREE. No Credit Card Required
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight" data-testid="schedule-title">
                <span className="text-[#FB923C]">FREE</span> 20-Minute Complimentary{' '}
                {V.color
                  ? <span style={{ color: V.color }} data-testid="schedule-variant-name">{brandName}</span>
                  : <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">{brandName}</span>}
              </h1>
              <p className="text-sm sm:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
                Pick a time, share {V.topic || 'your vision'} in our 6-question intake, and lock in your complimentary call with David.
              </p>
              {variant === 'health' && <p className="text-xs text-[#94A3B8] mt-3" data-testid="health-disclaimer">This is a coaching session, not medical advice. Please talk with your doctor about any medical concerns.</p>}
            </div>
          </section>
        )}

        <section className={embed ? 'pt-2 pb-12 sm:pb-16' : 'container-custom py-12 sm:py-16'}>
          <div className={embed ? 'container-custom' : ''}>
          {/* Steps indicator - shifted down with breathing room */}
          {step !== 'request' && (
          <div className="max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-xl shadow-sm px-3 py-4 sm:px-6 sm:py-5 mb-8 -mt-4 sm:-mt-8 relative z-10">
            <div className="flex items-center justify-center gap-2 sm:gap-4 overflow-x-auto" data-testid="step-indicator">
              {stepLabels.map((s, i) => (
                <React.Fragment key={s.n}>
                  {i > 0 && <div className={`w-6 sm:w-12 h-0.5 ${step >= s.n ? 'bg-[#1D4ED8]' : 'bg-[#E2E8F0]'}`}></div>}
                  <div className={`flex items-center gap-2 shrink-0 ${step >= s.n ? 'text-[#1D4ED8]' : 'text-[#94A3B8]'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step > s.n ? 'bg-[#1D4ED8] text-white shadow-md shadow-[#1D4ED8]/30' : step === s.n ? 'border-2 border-[#1D4ED8] text-[#1D4ED8] bg-[#EFF6FF]' : 'border-2 border-[#E2E8F0] text-[#94A3B8]'}`}>
                      {step > s.n ? <Check size={14} /> : s.n}
                    </div>
                    <span className="text-xs sm:text-sm font-semibold hidden sm:block">{s.l}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          )}

          {/* Step 1: Calendar */}
          {step === 1 && (
            <div className="max-w-md mx-auto bg-white p-6 sm:p-7 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80" data-testid="step-card-1">
              <div className="text-center mb-4">
                <h2 className="font-bold text-[#0A0F1C] text-lg">Pick a date</h2>
                <p className="text-xs text-[#94A3B8] mt-0.5">Closed Sundays &amp; 2nd Saturdays · 30-min slots · Bookings start the next day</p>
              </div>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-2 hover:bg-[#F1F5F9] rounded" data-testid="prev-month-btn"><ArrowLeft size={18} /></button>
                <h3 className="font-bold text-[#0A0F1C]">
                  {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-2 hover:bg-[#F1F5F9] rounded" data-testid="next-month-btn"><ArrowRight size={18} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(d => <div key={d} className="text-center text-xs font-medium text-[#94A3B8] py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`}></div>)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateObj = new Date(year, month, day);
                  const isPast = dateObj < today;
                  const isToday = dateObj.getTime() === today.getTime();
                  const dow = dateObj.getDay(); // 0=Sun ... 6=Sat
                  const isSunday = dow === 0;
                  const isSecondSaturday = dow === 6 && day >= 8 && day <= 14;
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isSelected = selectedDate === dateStr;
                  const isDisabled = isPast || isToday || isSunday || isSecondSaturday;
                  return (
                    <button
                      key={day}
                      onClick={() => !isDisabled && handleDateClick(day)}
                      disabled={isDisabled}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all mx-auto flex items-center justify-center ${isSelected ? 'bg-[#1D4ED8] text-white' : isDisabled ? 'text-[#D1D5DB] cursor-not-allowed' : 'hover:bg-[#EFF6FF] text-[#0A0F1C]'}`}
                      data-testid={`cal-day-${day}`}
                      title={isToday ? 'Bookings open the next day' : isSunday ? 'Closed on Sundays' : isSecondSaturday ? 'Closed on 2nd Saturday' : ''}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              {selectedDate && (
                <Button onClick={() => setStep(2)} className="btn-primary-blue w-full mt-4" data-testid="next-step-btn">
                  Continue <ArrowRight size={16} className="ml-2" />
                </Button>
              )}
              <button
                onClick={() => setStep('request')}
                className="w-full mt-3 text-xs font-semibold py-2 text-[#1D4ED8] hover:underline"
                data-testid="request-time-btn"
              >
                Need a time outside business hours? Request a custom time →
              </button>
            </div>
          )}

          {/* Step: Request a custom (out-of-hours) time */}
          {step === 'request' && (
            <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80" data-testid="request-step">
              {requestSent ? (
                <div className="text-center py-4" data-testid="request-sent">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#EFF6FF] to-[#F0F9FF] rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-blue-50">
                    <Mail size={28} className="text-[#1D4ED8]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0A0F1C] mb-2">Request sent!</h2>
                  <p className="text-[#475569] mb-5">Thanks, {form.name?.split(' ')[0] || 'there'}. We've sent your request for <strong>{reqDate} at {reqTime ? formatTime(reqTime) : ''}</strong> to David. You'll get an email as soon as it's reviewed. If approved, it'll include your Zoom link.</p>
                  <Button onClick={() => { setRequestSent(false); setReqDate(''); setReqTime(''); setStep(1); }} className="btn-primary-blue" data-testid="request-back-cal">Back to calendar</Button>
                </div>
              ) : (
                <>
                  <button onClick={() => setStep(1)} className="text-sm text-[#1D4ED8] mb-4 flex items-center gap-1 font-medium hover:underline" data-testid="request-back-btn"><ArrowLeft size={14} /> Back to calendar</button>
                  <div className="bg-gradient-to-r from-[#EFF6FF] to-[#FFF7ED] p-4 rounded-lg mb-6 border border-[#E2E8F0]">
                    <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-[#F97316]" /><h3 className="font-bold text-[#0A0F1C]">Request a time outside business hours</h3></div>
                    <p className="text-xs text-[#475569]">Tell us when works best. David will review your request and email you to approve it. If approved, you'll receive your Zoom link automatically. Please complete the 6 questions below so he can prepare.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0A0F1C] mb-1">Preferred date <span className="text-[#F97316]">*</span></label>
                      <Input type="date" min={tomorrowStr} value={reqDate} onChange={(e) => setReqDate(e.target.value)} data-testid="request-date" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0A0F1C] mb-1">Preferred time <span className="text-[#F97316]">*</span></label>
                      <Input type="time" value={reqTime} onChange={(e) => setReqTime(e.target.value)} data-testid="request-time" />
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    <Input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="request-name" />
                    <Input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="request-email" />
                    <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="request-phone" />
                  </div>
                  <div className="space-y-5 mb-5">
                    <div className="text-sm font-bold text-[#0A0F1C] flex items-center gap-2"><Sparkles size={16} className="text-[#F97316]" /> Quick Intake (all required)</div>
                    {intakeQuestions.map((q, idx) => {
                      const Icon = q.icon;
                      return (
                        <div key={q.key} data-testid={`request-intake-${idx}`}>
                          <label className="flex items-start gap-2 text-sm font-semibold text-[#0A0F1C] mb-2">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center text-xs">{idx + 1}</span>
                            <span className="flex items-center gap-2 leading-tight pt-1"><Icon size={14} className="text-[#1D4ED8] flex-shrink-0" />{q.label}</span>
                          </label>
                          {q.options ? (
                            <OptionPicker q={q} value={intake[q.key]} onChange={(v) => setIntake({ ...intake, [q.key]: v })} testPrefix="request-intake" />
                          ) : (
                            <Textarea placeholder={q.placeholder} value={intake[q.key]} onChange={(e) => setIntake({ ...intake, [q.key]: e.target.value })} rows={2} className="ml-0 sm:ml-9 w-full sm:w-[calc(100%-2.25rem)]" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Textarea placeholder="Anything else we should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mb-4" data-testid="request-notes" />
                  <Button onClick={submitTimeRequest} className="btn-primary-orange w-full" disabled={submitting || !intakeValid() || !reqDate || !reqTime || !form.name.trim() || !form.email.trim()} data-testid="request-submit">
                    {submitting ? 'Sending request…' : (<>Send request <ArrowRight size={16} className="ml-2" /></>)}
                  </Button>
                  <p className="text-[11px] text-[#94A3B8] text-center mt-3">This does not book a time yet. David will confirm by email.</p>
                </>
              )}
            </div>
          )}

          {/* Step 2: Time slots */}
          {step === 2 && (
            <div className="max-w-md mx-auto bg-white p-6 sm:p-7 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80" data-testid="step-card-2">
              <button onClick={() => setStep(1)} className="text-sm text-[#1D4ED8] mb-4 flex items-center gap-1 font-medium hover:underline" data-testid="back-to-date-btn"><ArrowLeft size={14} /> Change date</button>
              <h3 className="font-bold text-[#0A0F1C] mb-1">Available Times</h3>
              <p className="text-sm text-[#475569] mb-4 flex items-center gap-1"><Calendar size={14} /> {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
              {loadingSlots ? (
                <div className="py-8 text-center"><div className="w-8 h-8 border-3 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
              ) : slots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {slots.map((slot, i) => (
                    <button
                      key={slot.start}
                      onClick={() => setSelectedSlot(slot.start)}
                      className={`py-2 px-3 text-sm rounded-lg border transition-all font-medium ${selectedSlot === slot.start ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'}`}
                      data-testid={`time-slot-${i}`}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[#475569] text-center py-6">No available slots for this date</p>
              )}
              {selectedSlot && (
                <Button onClick={() => setStep(3)} className="btn-primary-blue w-full" data-testid="next-details-btn">
                  Continue <ArrowRight size={16} className="ml-2" />
                </Button>
              )}
              <button
                onClick={() => setStep('request')}
                className="w-full mt-3 text-xs font-semibold py-2 text-[#1D4ED8] hover:underline"
                data-testid="request-time-btn-2"
              >
                Don't see a time that works? Request a custom time →
              </button>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 3 && (
            <div className="max-w-md mx-auto bg-white p-6 sm:p-7 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80" data-testid="step-card-3">
              <button onClick={() => setStep(2)} className="text-sm text-[#1D4ED8] mb-4 flex items-center gap-1 font-medium hover:underline" data-testid="back-to-time-btn"><ArrowLeft size={14} /> Change time</button>
              <div className="bg-gradient-to-r from-[#EFF6FF] to-[#F0F9FF] p-4 rounded-xl mb-4 flex items-center gap-3 border border-[#DBEAFE]">
                <div className="w-9 h-9 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center shrink-0">
                  <Calendar size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#0A0F1C] truncate">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
                  <div className="text-xs text-[#475569]">{formatTime(selectedSlot)} · 20 min</div>
                </div>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!form.name.trim() || !form.email.trim()) {
                    toast.error('Name and email are required');
                    return;
                  }
                  setStep(4);
                }}
                className="space-y-3"
              >
                <input type="text" name="website" tabIndex={-1} autoComplete="off"
                  value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  aria-hidden="true" />
                <Input placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="booking-name" />
                <Input type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="booking-email" />
                <Input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="booking-phone" />
                <Textarea placeholder="Anything you'd like to discuss?" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="booking-notes" />
                <Button type="submit" className="btn-primary-blue w-full" data-testid="continue-intake-btn">
                  Continue to Intake <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
            </div>
          )}

          {/* Step 4: Intake (6 questions) */}
          {step === 4 && (
            <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80" data-testid="intake-step">
              <button onClick={() => setStep(3)} className="text-sm text-[#1D4ED8] mb-4 flex items-center gap-1 font-medium hover:underline" data-testid="back-to-details-btn"><ArrowLeft size={14} /> Edit details</button>

              <div className="bg-gradient-to-r from-[#EFF6FF] to-[#FFF7ED] p-4 rounded-lg mb-6 border border-[#E2E8F0]">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={16} className="text-[#F97316]" />
                  <h3 className="font-bold text-[#0A0F1C]">Quick Intake: required to confirm</h3>
                </div>
                <p className="text-xs text-[#475569]">These 6 questions help David prepare for your session and make it a 10/10 use of your 20 minutes.</p>
                <p className="text-xs font-semibold text-[#B45309] mt-2 flex items-start gap-1.5" data-testid="intake-required-notice">
                  <AlertCircle size={14} className="text-[#F97316] mt-0.5 flex-shrink-0" />
                  Please complete every question below (1-2 sentences each). The Confirm button stays locked until all 6 are answered.
                </p>
              </div>

              <div className="space-y-5">
                {intakeQuestions.map((q, idx) => {
                  const Icon = q.icon;
                  return (
                    <div key={q.key} data-testid={`intake-q-${idx}`}>
                      <label className="flex items-start gap-2 text-sm font-semibold text-[#0A0F1C] mb-2">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center text-xs">{idx + 1}</span>
                        <span className="flex items-center gap-2 leading-tight pt-1">
                          <Icon size={14} className="text-[#1D4ED8] flex-shrink-0" />
                          {q.label}
                        </span>
                      </label>
                      {q.options ? (
                        <OptionPicker q={q} value={intake[q.key]} onChange={(v) => setIntake({ ...intake, [q.key]: v })} testPrefix="intake" />
                      ) : (
                        <Textarea
                          placeholder={q.placeholder}
                          value={intake[q.key]}
                          onChange={(e) => setIntake({ ...intake, [q.key]: e.target.value })}
                          rows={3}
                          className="ml-0 sm:ml-9 w-full sm:w-[calc(100%-2.25rem)]"
                          data-testid={`intake-${q.key}-input`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button type="button" onClick={() => setStep(3)} className="btn-outline flex-1 sm:flex-initial" disabled={submitting} data-testid="intake-back-btn">
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  className="btn-primary-orange flex-1"
                  disabled={submitting || !intakeValid()}
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? 'Confirming...' : V.confirm}
                </Button>
              </div>
              {!intakeValid() && (
                <p className="text-xs text-[#94A3B8] mt-3 text-center" data-testid="intake-incomplete-hint">
                  Answer all 6 questions to confirm your session
                </p>
              )}
            </div>
          )}

          {/* Step 5: Confirmation */}
          {step === 5 && confirmed && (
            <div className="max-w-md mx-auto bg-white p-8 border border-[#E2E8F0] rounded-2xl shadow-lg shadow-slate-100/80 text-center" data-testid="booking-confirmed">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-green-50">
                <Check size={28} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A0F1C] mb-2">You're Booked!</h2>
              <p className="text-[#475569] mb-5">
                Your FREE 20-minute {V.lower} session is locked in. We've emailed you a confirmation
                {confirmed.zoom_join_url ? ' with your Zoom link below.' : '. David will reach out with the meeting link shortly.'}
              </p>

              {confirmed.zoom_join_url && (
                <div className="bg-[#0F172A] border border-[#1D4ED8]/40 rounded-xl p-4 mb-4 text-left" data-testid="zoom-link-section">
                  <div className="text-[10px] font-bold text-[#FB923C] tracking-wider uppercase mb-2 flex items-center gap-1">
                    <Video size={12} /> Join via Zoom
                  </div>
                  <a
                    href={confirmed.zoom_join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold text-sm px-4 py-2.5 rounded-lg w-full mb-2 transition-colors"
                    data-testid="zoom-join-btn"
                  >
                    <Video size={14} /> Join Zoom Meeting
                  </a>
                  <div className="text-[11px] text-[#94A3B8] break-all" data-testid="zoom-link-text">{confirmed.zoom_join_url}</div>
                  {confirmed.zoom_password && (
                    <div className="text-xs text-[#94A3B8] mt-2"><strong className="text-white">Passcode:</strong> {confirmed.zoom_password}</div>
                  )}
                </div>
              )}

              <a
                href={`${API_URL}/api/bookings/${confirmed.booking_id}/calendar.ics`}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F8FAFC] text-[#0A0F1C] border border-[#E2E8F0] hover:border-[#1D4ED8] font-semibold text-sm px-4 py-2.5 rounded-lg w-full mb-4 transition-colors"
                data-testid="add-to-calendar-btn"
              >
                <CalendarPlus size={14} /> Add to Calendar
              </a>

              <div className="bg-gradient-to-r from-[#EFF6FF] to-[#F0F9FF] border border-[#DBEAFE] p-4 rounded-xl mb-4 text-left">
                <div className="flex items-center gap-2 mb-2"><Calendar size={14} className="text-[#1D4ED8]" /><span className="text-sm font-semibold text-[#0A0F1C]">{new Date(confirmed.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-[#1D4ED8]" /><span className="text-sm font-semibold text-[#0A0F1C]">{formatTime(confirmed.time)}</span></div>
              </div>
              <p className="text-xs text-[#94A3B8]">Confirmation ID: <span className="font-mono">{confirmed.booking_id}</span></p>
              <CreateAccountPrompt name={form.name} email={form.email} />
            </div>
          )}
          </div>
        </section>
      </div>
    </ConditionalLayout>
  );
}

function ConditionalLayout({ embed, children, variant = 'business' }) {
  if (embed) return <>{children}</>;
  const V = VARIANTS[variant] || VARIANTS.business;
  return (
    <Layout title={`FREE 20-Minute Complimentary ${V.name}`} description={`Schedule your FREE 20-minute complimentary ${V.lower} session with David C. Ashley. Complete the 6-question intake to confirm your booking.`}>
      {children}
    </Layout>
  );
}
